import { Formik, Form } from "formik";
import { FC, useEffect, useRef, useState } from "react";
import { Step1 } from "./components/AddExperienceSteps/steps/Step1";
import { Step2 } from "./components/AddExperienceSteps/steps/Step2";
import { Step3 } from "./components/AddExperienceSteps/steps/Step3";
import { useLayout } from "../../../_metronic/layout/core";
import { showErrorAlert } from "../../helpers/alert";
import { useHistory } from "react-router-dom";
import { addImagesToExperience, createExperience, getAudiences, getConfidentialityLevels, getDurations } from "../../services/teamExperienceService";
import { LoaderOverlay } from "../../hooks/loader/LoaderOverlay";
import { ICatalogs } from "./models/GenericModel";
import { EXPERIENCE_STATUS } from "./models/ExperienceStatus";
import { validationSchemaForDraft, validationSchemaForPending } from "./components/AddExperienceSteps/utils";
import { useConfirmAction } from "../../hooks/utils/useConfirmAction";
import { RequiredInfoModal } from "../../helpers/RequiredInfoModal";
import { ExperienceDetail } from "./models/ExperienceSummaryModel";

export type Props = {
    step: number;
    onStep?: () => void;
    onBackStep: () => void;
    onNextStep: () => void;
    haveErrors: () => Promise<boolean>;
    onLoad?: (load:boolean) => void;
    onLoadMessage?: (msg:string) => void;
    onPause?: (show:boolean) => void;
    catalogs?: ICatalogs
    onSaveDraft?: () => void;
    experience?:ExperienceDetail;
}

const AddExperiencePage: FC = () => {
    const [isMounted, setIsMounted] = useState("");
    const [step, setStep] = useState(1);
    const formikRef = useRef();
    const [idSaved, setIdSaved] = useState(0);
    const [showModal, setShowModal] = useState(false);
    const [isLoader, setIsLoader] = useState(false);
    const [messageLoader, setMessageLoader] = useState("Loading...");
    const [messageModal, setMessageModal] = useState("");
    const history = useHistory()
    const confirm = useConfirmAction();
    // validate status
    const [statusDraft, setStatusDraft] = useState(false);
    // catalogos
    const [catalogs, setCatalogs] = useState<ICatalogs>({
        audiences: [],
        durations: [],
        confidentialities: [],
    });

    const { config, setLayout } = useLayout()
    const restoreRef = useRef(config)

    useEffect(() => {
        let isMounted = true;
        restoreRef.current = config
        setLayout({
            ...config,
            header: { ...config.header, display: true },
            aside: { ...config.aside, display: true },
            toolbar: { ...config.toolbar, display: false },
            footer: { ...config.footer, display: true },
        });
        const fetchData = async () => {
            setMessageLoader('Loading...');
            // setIsLoader(true);
            const audiences_ = await getAudiences();
            const durations_ = await getDurations();
            const confidentialities_ = await getConfidentialityLevels();
            if (!isMounted) return;
            setCatalogs({
                audiences: audiences_.results,
                durations: durations_.results,
                confidentialities: confidentialities_.results,
            })
            // setIsLoader(false);
        }
        fetchData();

        return () => {
            isMounted = false;
            setLayout(restoreRef.current)
        }
    }, [])

    const initialValues = {
        /* step1  */
        id: 0,
        experienceType: "new",
        suggest_id: null,
        /* step2  */
        host_id: 0,
        host_type: "partner",
        title: "",
        description: "",
        itinerary: "",
        what_is_included: "",
        availability_type: "by_date",
        upon_request_description: "",
        dates_data: [],
        duration_type: "2",
        duration: "",
        price_per_guest: 0,
        price_per_guest_text: "",
        price_type: 1,
        max_guests_per_group: "",
        enhancements_data: [],
        category_ids: [],
        city: "",
        location_address: "",
        arrival_notes: "",
        public: true,
        allowed_guest_ids: [],
        audience_type: 1,
        guest_capacity_adults: '0',
        guest_capacity_children: '0',
        guest_capacity_youth: '0',
        guest_capacity_infants: '0',
        total_min_guests: '',
        total_max_guests: '',
        minimum_age: "",
        important_information_guest: "",
        ideal_audience: "",
        host_presence: "",
        additional_team_member_ids: [],
        beneficiary_for_profit: true,
        beneficiary_check: false,
        payout_method: null,
        imageCover: null,
        galleryImages: [],
        optional_video_link: "",
        /* step3  */
        confidentiality_type: 1,
        confidentiality_check: false,
        policy_cancelation_check: false,
        status: EXPERIENCE_STATUS.PENDING,
    };

    const handleSubmit = async (values) => {
        let msgError = '';
        try {
            setMessageLoader('Saving your experience...');
            setIsLoader(true);
            let cloneObj = JSON.parse(JSON.stringify({...values}));
            delete cloneObj.imageCover;
            delete cloneObj.galleryImages;
            delete cloneObj.price_per_guest_text;
            delete cloneObj.experienceType;
            cloneObj.audience_type = Number(cloneObj.audience_type);
            cloneObj.duration_type = Number(cloneObj.duration_type);
            cloneObj.duration =  String(cloneObj.duration) != "" ? String(cloneObj.duration) : "1";
            cloneObj.price_type = Number(cloneObj.price_type || 1);
            cloneObj.max_guests_per_group = cloneObj.price_type === 2 && cloneObj.max_guests_per_group ? Number(cloneObj.max_guests_per_group) : null;
            cloneObj.upon_request_description = cloneObj.availability_type === 'by_request' ? cloneObj.upon_request_description : null;
            cloneObj.host_id = Number(cloneObj.host_id);
            if (values.suggest_id) {
                cloneObj.suggest_id = Number(values.suggest_id);
            }

            let id = idSaved;
            if(id == 0){
                msgError = 'Failed to save experience.';
                let response = await createExperience(cloneObj);
                id = response.id;
                setIdSaved(id);
            }else{
                msgError = 'Failed to update experience.';
            }
            // guardamos solo el cover validamos en caso de draft o no contenga imagenes
            let responseImgs = 0;
            if(values.imageCover){
                msgError = 'Failed to upload gallery images.';
                let fm = new FormData();
                fm.append('cover_image',values.imageCover)
                await addImagesToExperience(id, fm);
            }

            let galleryImages = (values.galleryImages ?? [])
                .map((img, idx) => ({ img, idx }))
                .filter(({ img }) => img instanceof File);

            if (galleryImages.length > 0) {
                for (let i = 0; i < galleryImages.length; i += 2) {
                    let fm = new FormData();
                    fm.append("upload_gallery_images", galleryImages[i].img);
                    fm.append("upload_gallery_images_orders", String(galleryImages[i].idx));
                    if (galleryImages[i + 1]) {
                        fm.append("upload_gallery_images", galleryImages[i + 1].img);
                        fm.append("upload_gallery_images_orders", String(galleryImages[i + 1].idx));
                    }
                    await addImagesToExperience(id, fm);
                    responseImgs += 1;
                }
            }
            setIsLoader(false); 
            if(id > 0){
                if(values.status == EXPERIENCE_STATUS.DRAFT){
                    history.replace('/my-experience/draft')
                }else if(values.status == EXPERIENCE_STATUS.PENDING || values.status == EXPERIENCE_STATUS.PUBLISHED){
                    history.replace('/my-experience/success')
                }
            }
        } catch (error) {
            setIsLoader(false);
            showErrorAlert({ title: '', message: `${msgError} ${error?.message ?? ''}` });
        }
    };

    const handleOnDraft = async () => {
        let continue_ = await confirm({ title: 'Save as Draft', message: 'Are you sure you want to save your progress as a draft?', btnTextAction: 'Save' });
        if(continue_){
            await setStatusDraft(true);
            let error = await haveErrors();
            if (!error) {
                await formikRef.current.setFieldValue("status",EXPERIENCE_STATUS.DRAFT);
                formikRef.current.submitForm();
            }else{
                await setStatusDraft(false);
            }
        }
    }

    const stepFields = {
        1: ["experienceType"],
        2: ["host_id","host_type","title", "description", "itinerary", "what_is_included", "availability_type", "dates_data", "duration_type", "duration", "price_per_guest_text", "price_type", "max_guests_per_group", "enhancements_data", "category_ids", "location_address",
            "arrival_notes", "public", "allowed_guest_ids", "audience_type", "guest_capacity_adults", "guest_capacity_children", "guest_capacity_youth", "guest_capacity_infants", "total_min_guests", "total_max_guests",
            "important_information_guest", "ideal_audience", "host_presence", "additional_team_member_ids", "beneficiary_for_profit", "beneficiary_check", "payout_method", "imageCover", "galleryImages", "optional_video_link"],
        3: ["confidentiality_type","confidentiality_check","policy_cancelation_check"],
    };

    const haveErrors = async () => {
        let fieldsToValidate = stepFields[step] || [];
        const isByDate = formikRef.current?.values?.availability_type === 'by_date';
        if (isByDate) {
            fieldsToValidate = fieldsToValidate.filter((key: string) => key !== 'duration_type' && key !== 'duration');
        } else {
            fieldsToValidate = fieldsToValidate.filter((key: string) => key !== 'dates_data');
        }
        const errors = await formikRef.current.validateForm();
        const stepErrors = Object.keys(errors).filter((key) =>
            fieldsToValidate.includes(key)
        );
        if(stepErrors.length>0){
            setMessageModal(`Please enter '${String(stepErrors[0]).replaceAll('_',' ')}' to save your progress.`);
            let input = document.querySelector(`input[name='${stepErrors[0]}']`);
            if(!input){
                input = document.querySelector(`textarea[name='${stepErrors[0]}']`);
            }
            if(!input){
                input = document.querySelector(`select[name='${stepErrors[0]}']`);
            }
            input?.focus();
            setShowModal(true);
            setTouchedForStep();
            return true;
        }
        return false;
    }

    const setTouchedForStep = () => {
        let fieldsToValidate = stepFields[step] || [];
        const isByDate = formikRef.current?.values?.availability_type === 'by_date';
        if (isByDate) {
            fieldsToValidate = fieldsToValidate.filter((key: string) => key !== 'duration_type' && key !== 'duration');
        } else {
            fieldsToValidate = fieldsToValidate.filter((key: string) => key !== 'dates_data');
        }
        let newTouched: any = {};
        for (const field of fieldsToValidate) {
            const value = formikRef.current.values[field];
            // caso 1: campo normal
            if (!Array.isArray(value)) {
                newTouched[field] = true;
                continue;
            }
            // caso 2: array de objetos (ej: dates)
            if (Array.isArray(value)) {
                newTouched[field] = value.map((item) => {
                    if (typeof item === "object" && item !== null) {
                        return Object.fromEntries(
                            Object.keys(item).map((key) => [key, true])
                        );
                    }
                    return true; // array de valores simples
                });
            }
        }
        formikRef.current.setTouched({
            ...formikRef.current.touched,
            ...newTouched,
        });
    };

    return (<>
        <Formik innerRef={formikRef} initialValues={initialValues} validationSchema={statusDraft ? validationSchemaForDraft : validationSchemaForPending} validateOnMount={true} onSubmit={handleSubmit}>
            {(formik) => {

                const nextStep = async () => {
                    let error = await haveErrors();
                    if (!error) {
                        setStep(step + 1);
                        window.scrollTo({ top: 0, behavior: "smooth" });
                    }
                };

                const backStep = () => {
                    if (step === 2 && formik.values.suggest_id) {
                        formik.setFieldValue('suggest_id', null);
                    }
                    setStep(step - 1);
                };
                
                return (<Form>
                    {step === 1 && (<Step1 catalogs={catalogs} onNextStep={nextStep} onBackStep={backStep} step={step} haveErrors={haveErrors} onPause={()=>{}} />)}
                    {step === 2 && (<Step2 catalogs={catalogs} onNextStep={nextStep} onBackStep={backStep} step={step} haveErrors={haveErrors} onPause={()=>{}} onSaveDraft={handleOnDraft} />)}
                    {step === 3 && (<Step3 catalogs={catalogs} onNextStep={nextStep} onBackStep={backStep} step={step} haveErrors={haveErrors} onPause={()=>{}} onSaveDraft={handleOnDraft} />)}
                </Form>);
            }}
        </Formik>
        <RequiredInfoModal open={showModal} message={messageModal} onClose={() => setShowModal(false)} />
        <LoaderOverlay visible={isLoader} message={messageLoader} />
        </>
    );
}

export { AddExperiencePage }